import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { APRProvider } from './context/APRContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ScreenVisibilityProvider } from './context/ScreenVisibilityContext';
import Layout from './components/Layout/Layout';
import Dashboard from './pages/Dashboard';
import Customers from './pages/Customers';
import ApplicationMasterData from './pages/ApplicationMasterData';
import MasterData from './pages/MasterData';
import CurrentRunRate from './pages/CurrentRunRate';
import CostingOverview from './pages/CostingOverview';
import Costing from './pages/Costing';
import Models from './pages/Models';
import APRModel from './pages/APRModel';
import APRRationalizationModels from './pages/APRRationalizationModels';
import APRExecution from './pages/APRExecution';
import ApplicationScoring from './pages/ApplicationScoring';
import ApplicationPortfolioQA from './pages/ApplicationPortfolioQA';
import ApplicationPortfolioScoring from './pages/ApplicationPortfolioScoring';
import DataExportReports from './pages/DataExportReports';
import ApplicationQuestionnaire from './pages/ApplicationQuestionnaire';
import ApplicationEvaluation from './pages/ApplicationEvaluation';
import Integration from './pages/Integration';
import HighLevelRoadMap from './pages/HighLevelRoadMap';
import UserManagement from './pages/UserManagement';
import UserAdministration from './pages/UserAdministration';
import Settings from './pages/Settings';
import APRSetup from './pages/APRSetup';
import Login from './components/Login';
import RouteGuard from './components/RouteGuard';
import Unauthorized from './components/Unauthorized';
// import ApiTest from './components/ApiTest'; // Commented out - not in use
import Dimensions from './pages/Dimensions';
import Components from './pages/Components';
import Attributes from './pages/Attributes';
import Questionnaire from './pages/Questionnaire';
import ScoringModel from './pages/ScoringModel';
import QuestionsManagement from './pages/QuestionsManagement';
import UserGuide from './pages/UserGuide';
import Initial4RDisposition from './pages/Initial4RDisposition';
import FinalApplicationDispositionModel from './pages/FinalApplicationDispositionModel';
import IntegrationApplicationMasterData from './pages/IntegrationApplicationMasterData';
import ApplicationDependencies from './pages/ApplicationDependencies';
import PhaseEffortPlanning from './pages/PhaseEffortPlanning';
import RoadmapSchedule from './pages/RoadmapSchedule';
import ResourceCapacity from './pages/ResourceCapacity';
import ResourceAllocation from './pages/ResourceAllocation';
import ResourceSkillsets from './pages/ResourceSkillsets';
import ResourceManagement from './pages/ResourceManagement';
import DispositionCostAnalysis from './pages/DispositionCostAnalysis';
import DispositionControl from './pages/DispositionControl';
import ApplicationPortfolio from './pages/ApplicationPortfolio';
import ImplementationRoadmap from './pages/ImplementationRoadmap';
import StartPage from './pages/StartPage';
import IntegrationGroupings from './pages/IntegrationGroupings';
// IntegrationRoadmapStatus removed - no longer needed
import BenefitRealization from './pages/BenefitRealization';
import FrameworkDefinition from './pages/FrameworkDefinition';
import FrameworkBuilder from './pages/FrameworkBuilder';
import HelpSupport from './pages/HelpSupport';
import RoleUtilization from './pages/RoleUtilization';
import ScreenVisibilityControl from './pages/ScreenVisibilityControl';
import './App.css';

const AppContent: React.FC = () => {
  const { isAuthenticated, currentUser, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Force redirect to proper landing page based on user role
  React.useEffect(() => {
    // Don't redirect while still loading to prevent flicker
    if (isLoading) {
      return;
    }

    if (isAuthenticated && currentUser) {
      const currentPath = location.pathname;

      // Skip redirect logic if on login page or transitioning
      if (currentPath === '/login' || currentPath === '/unauthorized') {
        return;
      }

      if (currentUser.role === 'Application Owner') {
        // Application Owner should always go to Q&A
        if (currentPath === '/' || currentPath === '/start' || !currentPath.startsWith('/evaluation')) {
          navigate('/evaluation/qa', { replace: true });
        }
      } else {
        // Administrator and User should go to Start page from root only
        if (currentPath === '/') {
          navigate('/start', { replace: true });
        }
      }
    }
  }, [isAuthenticated, currentUser, isLoading, navigate, location.pathname]);

  // If not authenticated (or still loading before auth check), show login
  // Keep Login mounted during the login process to preserve modal state
  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }

  // Show nothing during initial load only when authenticated (checking session)
  if (isLoading) {
    return null;
  }

  return (
    <Routes>
      {/* Redirect /login to appropriate page when already authenticated */}
      <Route path="/login" element={
        currentUser?.role === 'Application Owner' ? (
          <Navigate to="/evaluation/qa" replace />
        ) : (
          <Navigate to="/start" replace />
        )
      } />
      <Route path="/" element={<Layout />}>
        <Route index element={
          currentUser?.role === 'Application Owner' ? (
            <Navigate to="/evaluation/qa" replace />
          ) : (
            <Navigate to="/start" replace />
          )
        } />
        
        <Route path="start" element={
          <RouteGuard requiredPermission="canViewDashboard">
            <StartPage />
          </RouteGuard>
        } />
        
        <Route path="settings" element={
          <RouteGuard requiredPermission="canManageSettings">
            <Settings />
          </RouteGuard>
        } />
        
        <Route path="framework" element={
          <RouteGuard requiredPermission="canManageSettings">
            <APRSetup />
          </RouteGuard>
        } />
        
        <Route path="user-administration" element={
          <RouteGuard requiredPermission="canManageUsers">
            <UserAdministration />
          </RouteGuard>
        } />
        
        <Route path="framework/screen-visibility" element={
          <RouteGuard requiredPermission="canManageUsers">
            <ScreenVisibilityControl />
          </RouteGuard>
        } />

        <Route path="framework/user-management" element={
          <RouteGuard requiredPermission="canManageUsers">
            <UserManagement />
          </RouteGuard>
        } />
        
        <Route path="framework/dimensions" element={
          <RouteGuard requiredPermission="canViewAllMenus">
            <Dimensions />
          </RouteGuard>
        } />
        
        <Route path="framework/components" element={
          <RouteGuard requiredPermission="canViewAllMenus">
            <Components />
          </RouteGuard>
        } />
        
        <Route path="framework/attributes" element={
          <RouteGuard requiredPermission="canViewAllMenus">
            <Attributes />
          </RouteGuard>
        } />
        
        <Route path="framework/questionnaire" element={
          <RouteGuard requiredPermission="canViewAllMenus">
            <Questionnaire />
          </RouteGuard>
        } />
        
        <Route path="framework/questions" element={
          <RouteGuard requiredPermission="canManageSettings">
            <QuestionsManagement />
          </RouteGuard>
        } />
        
        <Route path="framework/scoring-model" element={
          <RouteGuard requiredPermission="canViewAllMenus">
            <ScoringModel />
          </RouteGuard>
        } />

        <Route path="framework/definition" element={
          <RouteGuard requiredPermission="canManageSettings">
            <FrameworkDefinition />
          </RouteGuard>
        } />

        <Route path="framework/builder" element={
          <RouteGuard requiredPermission="canManageSettings">
            <FrameworkBuilder />
          </RouteGuard>
        } />

        <Route path="customers" element={
          <RouteGuard requiredPermission="canViewAllMenus">
            <Customers />
          </RouteGuard>
        } />
        
        <Route path="master-data" element={
          <RouteGuard requiredPermission="canViewAllMenus">
            <MasterData />
          </RouteGuard>
        } />
        
        <Route path="customers/applications" element={
          <RouteGuard requiredPermission="canViewAllMenus">
            <ApplicationMasterData />
          </RouteGuard>
        } />
        
        <Route path="customers/costing" element={
          <RouteGuard requiredPermission="canViewAllMenus">
            <CostingOverview />
          </RouteGuard>
        } />
        
        <Route path="costing" element={
          <RouteGuard requiredPermission="canViewAllMenus">
            <Costing />
          </RouteGuard>
        } />
        
        <Route path="customers/costing/current-run-rate" element={
          <RouteGuard requiredPermission="canViewAllMenus">
            <CurrentRunRate />
          </RouteGuard>
        } />
        
        
        <Route path="models" element={
          <RouteGuard requiredPermission="canViewAllMenus">
            <Models />
          </RouteGuard>
        } />
        
        <Route path="execution" element={
          <RouteGuard requiredPermission="canRunAPRAnalysis">
            <APRExecution />
          </RouteGuard>
        } />
        
        <Route path="apr-model" element={
          <RouteGuard requiredPermission="canViewAllMenus">
            <APRModel />
          </RouteGuard>
        } />
        
        <Route path="apr-rationalization-models" element={
          <RouteGuard requiredPermission="canViewAllMenus">
            <APRRationalizationModels />
          </RouteGuard>
        } />
        
        <Route path="evaluation" element={
          currentUser?.role === 'Application Owner' ? (
            <Navigate to="/evaluation/qa" replace />
          ) : (
            <RouteGuard requiredPermission="canViewAPRScoring">
              <ApplicationEvaluation />
            </RouteGuard>
          )
        } />
        
        <Route path="evaluation/initial-4r-disposition" element={
          currentUser?.role === 'Application Owner' ? (
            <Navigate to="/evaluation/qa" replace />
          ) : (
            <RouteGuard requiredPermission="canViewAPRScoring">
              <Initial4RDisposition />
            </RouteGuard>
          )
        } />
        
        <Route path="evaluation/final-disposition" element={
          <RouteGuard requiredPermission="canViewAPRScoring">
            <FinalApplicationDispositionModel />
          </RouteGuard>
        } />
        
        <Route path="evaluation/qa" element={
          <RouteGuard requiredPermission="canViewAPRScoring">
            <ApplicationPortfolioQA />
          </RouteGuard>
        } />
        
        <Route path="evaluation/scoring" element={
          <RouteGuard requiredPermission="canViewAPRScoring">
            <ApplicationPortfolioScoring />
          </RouteGuard>
        } />
        
        <Route path="evaluation/reports" element={
          <RouteGuard requiredPermission="canViewAPRScoring">
            <DataExportReports />
          </RouteGuard>
        } />
        
        <Route path="questionnaire/:applicationId" element={
          <RouteGuard requiredPermission="canViewAPRScoring">
            <ApplicationQuestionnaire />
          </RouteGuard>
        } />
        
        {/* Keep old scoring route for backward compatibility */}
        <Route path="scoring" element={
          <RouteGuard requiredPermission="canViewAPRScoring">
            <ApplicationScoring />
          </RouteGuard>
        } />
        
        <Route path="integration-framework/application-portfolio" element={
          <RouteGuard requiredPermission="canViewAllMenus">
            <ApplicationPortfolio />
          </RouteGuard>
        } />
        <Route path="integration-framework/application-master-data" element={
          <RouteGuard requiredPermission="canViewAllMenus">
            <IntegrationApplicationMasterData />
          </RouteGuard>
        } />
        <Route path="integration-framework/application-dependencies" element={
          <RouteGuard requiredPermission="canViewAllMenus">
            <ApplicationDependencies />
          </RouteGuard>
        } />
        <Route path="integration-framework/phase-effort-planning" element={
          <RouteGuard requiredPermission="canViewAllMenus">
            <PhaseEffortPlanning />
          </RouteGuard>
        } />
        <Route path="integration-framework/roadmap-schedule" element={
          <RouteGuard requiredPermission="canViewAllMenus">
            <RoadmapSchedule />
          </RouteGuard>
        } />
        <Route path="integration-framework/disposition-control" element={
          <RouteGuard requiredPermission="canViewAllMenus">
            <DispositionControl />
          </RouteGuard>
        } />
        <Route path="integration-framework/disposition-cost-analysis" element={
          <RouteGuard requiredPermission="canViewAllMenus">
            <DispositionCostAnalysis />
          </RouteGuard>
        } />
        <Route path="integration-framework/resource-skillsets" element={
          <RouteGuard requiredPermission="canViewAllMenus">
            <ResourceSkillsets />
          </RouteGuard>
        } />
        <Route path="integration-framework/resource-capacity" element={
          <RouteGuard requiredPermission="canViewAllMenus">
            <ResourceCapacity />
          </RouteGuard>
        } />
        <Route path="integration-framework/resource-allocation" element={
          <RouteGuard requiredPermission="canViewAllMenus">
            <ResourceAllocation />
          </RouteGuard>
        } />
        <Route path="integration-framework/resource-management" element={
          <RouteGuard requiredPermission="canViewAllMenus">
            <ResourceManagement />
          </RouteGuard>
        } />
        <Route path="integration-framework/role-utilization" element={
          <RouteGuard requiredPermission="canViewAllMenus">
            <RoleUtilization />
          </RouteGuard>
        } />
        <Route path="integration-framework/roadmap-implementation-planning" element={
          <RouteGuard requiredPermission="canViewAllMenus">
            <IntegrationGroupings />
          </RouteGuard>
        } />
        
        <Route path="integration-framework" element={
          <RouteGuard requiredPermission="canViewAllMenus">
            <Integration />
          </RouteGuard>
        } />
        
        <Route path="integration" element={
          <RouteGuard requiredPermission="canViewAllMenus">
            <Integration />
          </RouteGuard>
        } />
        
        <Route path="roadmap/implementation" element={
          <RouteGuard requiredPermission="canViewAllMenus">
            <ImplementationRoadmap />
          </RouteGuard>
        } />
        <Route path="roadmap" element={
          <RouteGuard requiredPermission="canViewAllMenus">
            <HighLevelRoadMap />
          </RouteGuard>
        } />
        
        <Route path="dashboard" element={
          <RouteGuard requiredPermission="canViewDashboard">
            <Dashboard />
          </RouteGuard>
        } />
        
        <Route path="benefit-realization" element={
          <RouteGuard requiredPermission="canViewAllMenus">
            <BenefitRealization />
          </RouteGuard>
        } />
        
        <Route path="user-guide" element={<UserGuide />} />

        <Route path="help-support" element={<HelpSupport />} />

        <Route path="unauthorized" element={<Unauthorized />} />
      </Route>
    </Routes>
  );
};

// Wrapper component that only loads APRContext when authenticated
const AuthenticatedAPRProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  // Don't load APRContext until we know we're authenticated
  if (isLoading || !isAuthenticated) {
    return <>{children}</>;
  }

  return <APRProvider>{children}</APRProvider>;
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <ScreenVisibilityProvider>
          <AuthenticatedAPRProvider>
            <div className="App">
              {/* <ApiTest /> */}
              <AppContent />
            </div>
          </AuthenticatedAPRProvider>
        </ScreenVisibilityProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
